import type {
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  ISeriesPrimitive,
  PrimitivePaneViewZOrder,
  SeriesAttachedParameter,
  Time,
} from 'lightweight-charts';
import { CHIP_FONT, chipText, type LayoutChip, type LayoutLeader, type MarkerLayoutRegistry } from './markerLayout';

/**
 * Draws the shared layout's "+N" chips: the markers a bar side had no room for
 * at this zoom. Hovering one lists what it hides (ChartView's tooltip reads
 * `registry.hitTest`). Attached last so the chips sit on top.
 */
class ChipRenderer implements IPrimitivePaneRenderer {
  private _chips: readonly LayoutChip[] = [];

  update(chips: readonly LayoutChip[]): void {
    this._chips = chips;
  }

  draw(target: any): void {
    if (this._chips.length === 0) return;
    target.useBitmapCoordinateSpace((scope: any) => {
      const ctx = scope.context as CanvasRenderingContext2D;
      const hr = scope.horizontalPixelRatio as number;
      const vr = scope.verticalPixelRatio as number;
      ctx.save();
      ctx.globalAlpha = 1;
      ctx.shadowBlur = 0;
      ctx.setLineDash([]);
      ctx.font = CHIP_FONT.replace(/(\d+)px/, (_, n) => `${Number(n) * vr}px`);
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      for (const c of this._chips) {
        const x = c.left * hr;
        const y = c.top * vr;
        const w = c.w * hr;
        const h = c.h * vr;
        const r = Math.min(h / 2, 6 * Math.min(hr, vr));
        ctx.beginPath();
        ctx.moveTo(x + r, y);
        ctx.arcTo(x + w, y, x + w, y + h, r);
        ctx.arcTo(x + w, y + h, x, y + h, r);
        ctx.arcTo(x, y + h, x, y, r);
        ctx.arcTo(x, y, x + w, y, r);
        ctx.closePath();
        ctx.fillStyle = 'rgba(15, 23, 42, 0.92)';
        ctx.fill();
        ctx.lineWidth = Math.max(1, hr);
        ctx.strokeStyle = 'rgba(148, 163, 184, 0.85)';
        ctx.stroke();
        ctx.fillStyle = '#e2e8f0';
        ctx.fillText(chipText(c), x + w / 2, y + h / 2 + 0.5 * vr);
      }
      ctx.restore();
    });
  }
}

class ChipPaneView implements IPrimitivePaneView {
  private _renderer = new ChipRenderer();
  constructor(private _source: MarkerChipPrimitive) {}
  zOrder(): PrimitivePaneViewZOrder {
    return 'top';
  }
  renderer(): IPrimitivePaneRenderer {
    this._renderer.update(this._source.chips());
    return this._renderer;
  }
}

export class MarkerChipPrimitive implements ISeriesPrimitive<Time> {
  private _view = new ChipPaneView(this);
  private _requestUpdate?: () => void;
  private _unsub: (() => void) | null = null;

  constructor(private _registry: MarkerLayoutRegistry) {}

  attached(param: SeriesAttachedParameter<Time>): void {
    this._requestUpdate = param.requestUpdate;
    // An item set changed somewhere: repaint, so the chips follow.
    this._unsub = this._registry.onInvalidate(() => this._requestUpdate?.());
  }

  detached(): void {
    this._unsub?.();
    this._unsub = null;
    this._requestUpdate = undefined;
  }

  paneViews(): IPrimitivePaneView[] {
    return [this._view];
  }

  updateAllViews(): void {
    /* the layout is read at render time */
  }

  chips(): readonly LayoutChip[] {
    return this._registry.layout().chips;
  }
}

/**
 * The layout's leaders: 1px dotted, low-contrast lines in the marker's hue from
 * the exact anchor (bar x, price) to the near edge of its box. zOrder 'normal'
 * puts them over the candles (crossing the bar's own wick is fine) and UNDER
 * every marker, which all draw at 'top'.
 */
class LeaderRenderer implements IPrimitivePaneRenderer {
  private _leaders: readonly LayoutLeader[] = [];

  update(leaders: readonly LayoutLeader[]): void {
    this._leaders = leaders;
  }

  draw(target: any): void {
    if (this._leaders.length === 0) return;
    target.useBitmapCoordinateSpace((scope: any) => {
      const ctx = scope.context as CanvasRenderingContext2D;
      const hr = scope.horizontalPixelRatio as number;
      const vr = scope.verticalPixelRatio as number;
      ctx.save();
      ctx.globalAlpha = LEADER_ALPHA;
      ctx.shadowBlur = 0;
      ctx.lineWidth = Math.max(1, Math.floor(hr));
      ctx.setLineDash([1 * hr, 2 * hr]);
      ctx.lineCap = 'butt';
      for (const l of this._leaders) {
        // Snap to the pixel centre so a 1px line stays crisp.
        const x1 = Math.round(l.x1 * hr) + 0.5;
        const x2 = Math.round(l.x2 * hr) + 0.5;
        ctx.strokeStyle = l.color;
        ctx.beginPath();
        ctx.moveTo(x1, l.y1 * vr);
        ctx.lineTo(x2, l.y2 * vr);
        ctx.stroke();
      }
      ctx.restore();
    });
  }
}

/** Leaders are context, not content: faint. */
export const LEADER_ALPHA = 0.55;

class LeaderPaneView implements IPrimitivePaneView {
  private _renderer = new LeaderRenderer();
  constructor(private _source: MarkerLeaderPrimitive) {}
  zOrder(): PrimitivePaneViewZOrder {
    return 'normal';
  }
  renderer(): IPrimitivePaneRenderer {
    this._renderer.update(this._source.leaders());
    return this._renderer;
  }
}

export class MarkerLeaderPrimitive implements ISeriesPrimitive<Time> {
  private _view = new LeaderPaneView(this);

  constructor(private _registry: MarkerLayoutRegistry) {}

  attached(_param: SeriesAttachedParameter<Time>): void {
    /* the chip primitive already repaints on invalidate */
  }

  detached(): void {
    /* nothing held */
  }

  paneViews(): IPrimitivePaneView[] {
    return [this._view];
  }

  updateAllViews(): void {
    /* the layout is read at render time */
  }

  leaders(): readonly LayoutLeader[] {
    return this._registry.layout().leaders;
  }
}
